import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "utils/http";
import { BUSINESS_CUSTOMER } from "constants/api";

export interface IBusinessCustomersResponse extends ApiResponse {
  data: IBusinessCustomers;
}

export interface IFetchBusinessCustomersArgs extends ReduxAction {
  page: number;
  queries: Partial<IBusinessCustomerSearchParams>;
}

export interface IFetchSingleBusinessCustomerResponse extends ApiResponse {
  data: IBusinessCustomerDetails;
}

export interface IFetchSingleBusinessCustomerArgs extends ReduxAction {
  id: number;
}

export interface IRestrictBusinessCustomerArgs extends ReduxAction {
  id: number;
}

export const fetchBusinessCustomers = createAsyncThunk(
  BUSINESS_CUSTOMER.LIST,
  async (
    { page, queries, onFailure }: IFetchBusinessCustomersArgs,
    { rejectWithValue }
  ) => {
    try {
      const searchParams = new URLSearchParams();

      if (Object.values(queries)?.length) {
        Object.entries(queries).forEach(([key, value]) => {
          if (value) {
            searchParams.append(`${key}`, String(value));
          }
        });
      }

      const { data }: IBusinessCustomersResponse = await axios.get(
        `${BUSINESS_CUSTOMER.LIST}?page=${page}`,
        {
          params: searchParams,
        }
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchSingleBusinessCustomer = createAsyncThunk(
  BUSINESS_CUSTOMER.DETAIL,
  async (
    { id, onFailure }: IFetchSingleBusinessCustomerArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IFetchSingleBusinessCustomerResponse = await axios.get(
        BUSINESS_CUSTOMER.DETAIL.replace(":id", String(id))
      );

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

// `partial_restriction` is the only status the endpoint accepts.
export const restrictBusinessCustomer = createAsyncThunk(
  BUSINESS_CUSTOMER.RESTRICT,
  async (
    { id, onSuccess, onFailure }: IRestrictBusinessCustomerArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        BUSINESS_CUSTOMER.RESTRICT.replace(":id", String(id)),
        { status: "partial_restriction" }
      );

      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

// The unrestrict endpoint intentionally takes no body.
export const unrestrictBusinessCustomer = createAsyncThunk(
  BUSINESS_CUSTOMER.UNRESTRICT,
  async (
    { id, onSuccess, onFailure }: IRestrictBusinessCustomerArgs,
    { rejectWithValue }
  ) => {
    try {
      const { message }: ApiResponse = await axios.post(
        BUSINESS_CUSTOMER.UNRESTRICT.replace(":id", String(id))
      );

      if (onSuccess) onSuccess(message);
      return message;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);
